import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getToken, UpdateUserDetails, User } from "../../../utils";
import { CustomSnackbar } from "../../../components/shared";

import { useAuth } from "utils/context";
import { ConfirmationDialog } from "features/reusable";

interface AmountDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  initialData?: User | null;
}

export const AmountDialog: React.FC<AmountDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const [addAmount, setAddAmount] = useState(0);
  const [subtractAmount, setSubtractAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSuccess, setSnackbarSuccess] = useState(true);
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  
  // State to manage the confirmation dialog
  const [openConfirm, setOpenConfirm] = useState(false);

  // Reset amounts only when the dialog is opened
  useEffect(() => {
    if (open) {
      setAddAmount(0);
      setSubtractAmount(0);
    }
  }, [open]);

  useEffect(() => {
    const retrievedToken = getToken();
    setToken(retrievedToken);
  }, []);


  const handleSaveRequest = () => {

    if (addAmount - subtractAmount !== 0) {
      setOpenConfirm(true);
    } else {
      onClose(); 
    }
  };


  const handleConfirmSave = async () => {
    setOpenConfirm(false); 
    setLoading(true);
    try {
      if (initialData && user) {
        const newAmount = addAmount - subtractAmount;
        await UpdateUserDetails(
          {
            amount: newAmount,
            userIDAdmin: user.userID,
            userIDUpdateUser: initialData.userID,
          },
          "amount",
          user.userID,
          token
        );

        onSave();
        onClose();
        setSnackbarMessage("Montant mis à jour avec succès!");
        setSnackbarSuccess(true);
      }
    } catch (error) {
      setSnackbarMessage("Erreur lors de la mise à jour du montant");
      setSnackbarSuccess(false);
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const netChange = (addAmount || 0) - (subtractAmount || 0);

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Gérer le montant de l'utilisateur</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="center"
            gap={2}
            width="300px"
          >
            <TextField
              label="Ajouter un montant"
              name="addAmount"
              type="number"
              value={addAmount}
              onChange={(e) => setAddAmount(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Soustraire un montant (valeur absolue)"
              name="subtractAmount"
              type="number"
              inputProps={{ min: 0 }}
              value={subtractAmount}
              onChange={(e) => setSubtractAmount(parseFloat(e.target.value) || 0)}
              fullWidth
            />
            {initialData && (
              <Typography variant="body2" color="textSecondary">
                Montant actuel: {initialData.amount} | Changement Net: {netChange}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button onClick={handleSaveRequest} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmSave}
        title="Confirmer la modification du montant"
        description={`Vous êtes sur le point d'appliquer un changement net de ${netChange}. Le nouveau solde de l'utilisateur sera de ${initialData ? initialData.amount + netChange : netChange}. Voulez-vous continuer?`}
        confirmButtonText="Confirmer"
        confirmButtonColor="primary"
      />

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        isSuccess={snackbarSuccess}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};